<script setup lang="ts">
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useRouter } from 'vue-router';

import { useTranslation } from 'i18next-vue';

import { useAccessControlStore } from '@peanuds/app-shell';
import { formatCurrency, formatIban, normalizeIban } from '@peanuds/shared';
import { useAccountBankDetails } from '@peanuds/shared-state';

import Tag from 'primevue/tag';
import Divider from 'primevue/divider';

import {
  AddPlusCircleBlackIcon as AddPlusCircleIcon,
  SharePopover,
  UiButton,
  UiCopyToggleButton,
  currencyCoinIcons,
  getCurrencyCoinIcon,
  useSharePopover,
} from '@peanuds/ui';

const {
  label,
  accountId,
  currency,
  expanded,
  balance = 0,
  isPending = false,
  pendingLabel = 'Setting up your IBAN',
  variant = 'default',
} = defineProps<{
  label: string;
  accountId?: string | null;
  currency?: string;
  expanded?: boolean;
  balance?: number;
  isPending?: boolean;
  pendingLabel?: string;
  variant?: 'default' | 'multiple-currencies';
}>();

const { t } = useTranslation();

const bankDetails = useAccountBankDetails(() =>
  variant === 'multiple-currencies' ? null : accountId,
);
const iban = computed(() => bankDetails.value?.iban ?? '');
const isEmpty = computed(() => !iban.value || iban.value.trim() === '');

const isMultipleCurrencies = computed(() => variant === 'multiple-currencies');
const isPendingAccount = computed(() => isPending && !isMultipleCurrencies.value);

const accessControlStore = useAccessControlStore();
const { isOnboarded } = storeToRefs(accessControlStore);
const canShowIban = computed(() => isOnboarded.value && !isPendingAccount.value);
const router = useRouter();

const currencyIcon = computed(() => {
  if (isMultipleCurrencies.value) {
    return null;
  }

  return getCurrencyCoinIcon(currency ?? '') ?? currencyCoinIcons.EUR;
});

const tagLabel = computed(() => {
  return currency ? `${currency} ${label}` : label;
});

const formattedBalance = computed(() => {
  const amount = isOnboarded.value && !isPendingAccount.value ? balance : 0;
  return formatCurrency(amount, currency as 'EUR' | 'USD');
});

const formattedIban = computed(() => formatIban(iban.value));

const canShareIban = computed(() => !isEmpty.value && isOnboarded.value && !isPendingAccount.value);

const { sharePopoverRef, toggleShare } = useSharePopover();

const handleShare = (event: MouseEvent) => {
  // Prevent card click (parent) from firing when clicking Share.
  event.stopPropagation();
  event.preventDefault();
  if (!canShareIban.value) return;
  toggleShare(event);
};

const handleOpenAccount = () => {
  if (isPendingAccount.value) {
    return;
  }

  router.push({ name: 'open-account' });
};
</script>

<template>
  <div
    :class="{
      'iban-swiper__content': true,
      'iban-swiper__content--expanded': expanded,
      'iban-swiper__content--pending': isPendingAccount,
    }"
  >
    <!-- Expanded State -->
    <template v-if="expanded && isMultipleCurrencies">
      <div class="iban-swiper__currencies">
        <div class="iban-swiper__currencies-head">
          <div
            class="iban-swiper__currency-icon-wrapper iban-swiper__currency-icon-wrapper--stacked"
          >
            <div class="iban-swiper__multi-icon iban-swiper__multi-icon--left">
              <img :src="currencyCoinIcons.EUR" alt="" class="iban-swiper__currency-icon" />
            </div>
            <div class="iban-swiper__multi-icon iban-swiper__multi-icon--center">
              <img :src="currencyCoinIcons.USD" alt="" class="iban-swiper__currency-icon" />
            </div>
            <div class="iban-swiper__multi-icon iban-swiper__multi-icon--right">
              <img :src="currencyCoinIcons.GBP" alt="" class="iban-swiper__currency-icon" />
            </div>
          </div>
          <Tag
            :value="label"
            rounded
            severity="info"
            class="ibans-hero-card__tag ibans-hero-card__tag--open"
          />
        </div>

        <UiButton
          variant="ghost"
          severity="secondary"
          :label="t('ibanSwiper.labels.open_new_account')"
          class="iban-swiper__open-account-button"
          @click.stop="handleOpenAccount"
        >
          <template #icon>
            <AddPlusCircleIcon />
          </template>
        </UiButton>
      </div>
    </template>

    <template v-else-if="expanded">
      <div class="iban-swiper__header">
        <div class="ibans-hero-card__top iban-swiper__header-left">
          <div class="iban-swiper__currency-icon-wrapper">
            <img
              v-if="currencyIcon"
              :src="currencyIcon"
              alt=""
              class="iban-swiper__currency-icon"
            />
          </div>
          <Tag :value="tagLabel" rounded severity="info" class="ibans-hero-card__tag" />
        </div>

        <UiButton
          :disabled="!canShareIban"
          variant="ghost"
          class="iban-swiper__action-button"
          aria-label="Share"
          @click.stop="handleShare"
        >
          <template #icon>
            <i class="pi pi-share-alt" />
          </template>
        </UiButton>
      </div>

      <div class="iban-swiper__main">
        <div class="iban-swiper__balance-wrapper">
          <p class="iban-swiper__balance">{{ formattedBalance }}</p>
        </div>

        <div class="iban-swiper__footer">
          <div class="iban-swiper__divider-wrapper">
            <Divider />
          </div>

          <div v-if="isPendingAccount" class="iban-swiper__iban-row iban-swiper__iban-row--pending">
            <p class="iban-swiper__pending-text">{{ pendingLabel }}</p>
          </div>

          <div
            v-else-if="isEmpty"
            class="iban-swiper__iban-row"
            :class="{ 'cannot-open-account': !isOnboarded }"
          >
            <p class="iban-swiper__empty-text">Open {{ currency }} Account</p>
            <UiButton
              variant="ghost"
              severity="primary"
              class="iban-swiper__add-button"
              aria-label="Open account"
              iconOnly
              :disabled="!isOnboarded"
              @click.stop="handleOpenAccount"
            >
              <template #icon>
                <AddPlusCircleIcon />
              </template>
            </UiButton>
          </div>

          <div v-else class="iban-swiper__iban-row">
            <p class="iban-swiper__iban-text">
              {{ canShowIban ? formattedIban : '--' }}
            </p>
            <UiCopyToggleButton
              button-size="2.375rem"
              tooltip-text="IBAN"
              :copy-text="normalizeIban(iban)"
              :disabled="!canShowIban"
              icon-dark
              class="iban-swiper__copy-button"
            />
          </div>
        </div>
      </div>
    </template>

    <template v-else>
      <div class="iban-swiper__collapsed-content">
        <div
          class="ibans-hero-card__top ibans-hero-card__top--with-balance iban-swiper__collapsed-left"
        >
          <div
            class="iban-swiper__currency-icon-wrapper"
            :class="{ 'iban-swiper__currency-icon-wrapper--stacked': isMultipleCurrencies }"
          >
            <template v-if="isMultipleCurrencies">
              <div class="iban-swiper__multi-icon iban-swiper__multi-icon--left">
                <img :src="currencyCoinIcons.EUR" alt="" class="iban-swiper__currency-icon" />
              </div>
              <div class="iban-swiper__multi-icon iban-swiper__multi-icon--center">
                <img :src="currencyCoinIcons.USD" alt="" class="iban-swiper__currency-icon" />
              </div>
              <div class="iban-swiper__multi-icon iban-swiper__multi-icon--right">
                <img :src="currencyCoinIcons.GBP" alt="" class="iban-swiper__currency-icon" />
              </div>
            </template>
            <img
              v-else-if="currencyIcon"
              :src="currencyIcon"
              alt=""
              class="iban-swiper__currency-icon"
            />
          </div>
          <Tag
            :value="tagLabel"
            severity="secondary"
            class="ibans-hero-card__tag ibans-hero-card__tag--collapsed"
            :class="{ 'ibans-hero-card__tag--open': isMultipleCurrencies }"
          />
          <p
            v-if="isPendingAccount"
            class="iban-swiper__pending-text iban-swiper__pending-text--collapsed"
          >
            {{ pendingLabel }}
          </p>
          <p
            v-else-if="!isMultipleCurrencies"
            class="iban-swiper__balance iban-swiper__balance--collapsed"
          >
            {{ formattedBalance }}
          </p>
        </div>

        <UiButton
          v-if="!isMultipleCurrencies"
          :disabled="!canShareIban"
          variant="ghost"
          severity="primary"
          class="iban-swiper__action-button"
          aria-label="Share"
          @click.stop="handleShare"
        >
          <template #icon>
            <i class="pi pi-share-alt" />
          </template>
        </UiButton>

        <UiButton
          v-else
          variant="ghost"
          severity="primary"
          class="iban-swiper__add-button"
          aria-label="Open account"
          iconOnly
          @click.stop="handleOpenAccount"
        >
          <template #icon>
            <AddPlusCircleIcon />
          </template>
        </UiButton>
      </div>
    </template>

    <SharePopover v-if="canShareIban" ref="sharePopoverRef" :bank-details="bankDetails" />
  </div>
</template>

<style scoped lang="scss">
.iban-swiper {
  &__content {
    height: 100%;
    width: 100%;
    display: flex;
    border-radius: $radius-16;
    padding: 0.25rem;

    &--expanded {
      flex-direction: column;
      padding: $spacing-md;
    }

    &--pending {
      :deep(.p-tag),
      :deep(.p-button) {
        opacity: 0.7;
      }
    }
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: relative;
    width: 100%;

    &-left {
      display: flex;
      align-items: center;
      gap: $static-space-2; // 8px
      flex: 1;
      min-width: 0;
    }
  }

  &__main {
    display: flex;
    flex-direction: column;
    flex: 1;
    width: 100%;
  }

  &__currency-icon {
    width: 100%;
    height: 100%;
    display: block;

    &-wrapper {
      width: 24px;
      height: 24px;
      border-radius: $radius-999;
      display: flex;
      justify-content: center;
      overflow: visible;
      flex-shrink: 0;
      position: relative; // For absolute positioning of tag

      // Safari fix: Force GPU compositing layer for SVGs inside Swiper
      transform: translateZ(0);
      -webkit-backface-visibility: hidden;
      backface-visibility: hidden;

      /* Align with IbansHeroSection `ibans-hero-card__top` design */
      background: color-mix(in srgb, var(--p-surface-0) 12%, transparent);
      border: 1px solid transparent;

      &--stacked {
        width: auto;
        height: 30px;
        justify-content: flex-start;
        background: transparent;
        border-color: transparent;
      }
    }
  }

  &__multi-icon {
    width: 1.85rem;
    // Avoid clipping SVG filters (coin shadows) and align with hero cards.
    overflow: visible;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: transparent;

    &--left {
      z-index: 1;
    }

    &--center {
      margin-left: $static-space-3 * -1; // -12px
      margin-right: $static-space-3 * -1; // -12px
      z-index: 2;
    }

    &--right {
      z-index: 3;
    }
  }

  .ibans-hero-card__top {
    display: flex;
    align-items: center;
    gap: $spacing-xs;
    min-width: 0;

    &--with-balance {
      .iban-swiper__balance {
        margin-left: auto;
      }
    }
  }

  &__content .ibans-hero-card__tag {
    white-space: nowrap;
    background: $color-tag-standard;
    color: $color-brand-600;
    min-width: 0;
    overflow: hidden;
    flex: 0 1 auto;

    :deep(.p-tag-value) {
      color: inherit;
      display: block;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    :deep(.p-tag-label) {
      display: block;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    &--collapsed {
      flex: 1;
      max-width: 100%;
    }

    &--open {
      z-index: 200;
      background: $color-tag-blue;
      color: $color-tag-on-blue;
    }
  }

  &__action-button {
    color: var(--p-surface-700);
    flex-shrink: 0;
  }

  &__balance {
    font-family: var(--font-family-base);
    font-size: $font-size-lg;
    font-weight: $font-weight-semibold;
    line-height: 1;
    letter-spacing: -0.072px;
    color: $color-text-primary;
    white-space: nowrap;
    margin: 0;

    &--collapsed {
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-align: right;
      text-overflow: ellipsis;
    }

    &-wrapper {
      padding-top: $spacing-sm;
    }
  }

  &__footer {
    margin-top: auto;
  }

  &__divider-wrapper {
    margin-top: $spacing-sm;
    width: 100%;

    :deep(.p-divider) {
      margin: 0;
      border-color: $color-table-divider;
    }
  }

  &__iban-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: $spacing-md;
    padding-top: $spacing-sm;

    &--pending {
      justify-content: flex-start;
      padding: 0.75rem 0;
    }

    &.cannot-open-account {
      opacity: 0.75;
      pointer-events: none;
    }
  }

  &__pending-text {
    font-family: var(--font-family-base);
    font-size: $text-xs;
    font-weight: $font-weight-regular;
    line-height: 1;
    letter-spacing: -0.048px;
    color: var(--p-surface-500);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin: 0;

    &--collapsed {
      flex: 1;
      min-width: 0;
      text-align: right;
    }
  }

  &__iban-text {
    font-family: var(--font-family-base);
    font-size: $font-size-sm;
    font-weight: $font-weight-medium;
    line-height: 1;
    letter-spacing: -0.056px;
    color: $color-text-primary;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1;
    min-width: 0;
    margin: 0;
  }

  &__copy-button {
    flex-shrink: 0;
  }

  &__collapsed-content .ibans-hero-card__tag--open {
    margin-left: -18px;
  }

  &__collapsed {
    &-content {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      gap: $static-space-3; // 12px
    }

    &-left {
      display: flex;
      align-items: center;
      gap: $static-space-2; // 8px (match hero-card top)
      flex: 1;
      min-width: 0;
    }
  }

  &__empty-text {
    font-family: var(--ApplicationMain, var(--font-family-base));
    font-size: $text-sm;
    font-style: normal;
    font-weight: $font-weight-regular;
    line-height: $leading-none;
    letter-spacing: var(--p-letter-spacing-tight-1);
    color: var(--p-text-color, var(--p-surface-700));
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1;
    min-width: 0;
    margin: 0;
  }

  &__add-button {
    flex-shrink: 0;
  }

  &__currencies {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: $spacing-sm;
    align-items: center;
    justify-content: center;
    width: 100%;
  }

  &__currencies-head {
    display: flex;
    gap: $spacing-s;
    align-items: center;
  }

  &__open-account-button {
    color: $color-text-primary;
  }
}
</style>
